import * as React from "react"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import { IAdmTableColumn, IAdmTableData } from "../../util/interface"
import { createUseStyles, useTheme } from "react-jss"
import { useAppSelector } from "../../util/hooks"
import { admDataSelector } from "./DataMetaDataSlice"
import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { v4 as uuidv4 } from "uuid"
const useStyles = createUseStyles({
  wrapper: {
    textAlign: "center !important",
  },
  root: {
    "& .MuiTableCell-head": {
      backgroundColor: "#fbfbfb",
    },
  },
  borderRight: {
    borderRight: "1px solid lightgray !important",
  },
  textAlign: {
    textAlign: "center",
  },
  heading: {
    fontSize: 16,
    fontWeight: 600,
  },
  resultDiv: {
    height: 25,
    width: 70,
    padding: "2px 10px",
    borderRadius: 5,
    margin: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "auto",
    marginRight: "auto",
  },
  passDiv: {
    backgroundColor: "#27af8d",
    color: "white",
  },
  failedDiv: {
    backgroundColor: "#fcc",
    color: "#da1818",
    fontSize: 14,
  },
  tableCell: {
    // top: 57,
  },
  tableCellWithMinWidth: {
    minWidth: (props: any) => props.minWidth,
  },
})

const ResultDiv = styled.div`
  height: 25px;
  width: 70px;
  padding: 2px 10px;
  border-radius: 5px;
  margin: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
`

const PassDiv = styled(ResultDiv)`
  background-color: #27af8d;
  color: white;
`

const FailedDiv = styled(ResultDiv)`
  background-color: #fcc;
  color: #da1818;
  font-size: 14px;
`

const columns: IAdmTableColumn[] = [
  { id: "BASE_TABLE", label: "Base Table Name", minWidth: 170, align: "left" },
  { id: "COUNT_IN_BASE_TABLE", label: "Counts", minWidth: 100 },
  {
    id: "MAPPED_TABLE",
    label: "Mapped Table Name",
    minWidth: 170,
    align: "left",
  },
  {
    id: "COUNT_OF_MAPPED",
    label: "Counts",
    minWidth: 170,
    align: "center",
  },
  {
    id: "MATCH_VOLUME_PERCENTAGE",
    label: "Matched Volume %",
    minWidth: 170,
    align: "center",
    format: (value: number) => value.toFixed(1),
  },
  {
    id: "QC_STATUS",
    label: "QC Status",
    minWidth: 170,
    align: "center",
  },
]

// const createData = (
//   BASE_TABLE: string,
//   COUNT_IN_BASE_TABLE: number,
//   MAPPED_TABLE: string,
//   COUNT_OF_MAPPED: number,
//   MATCH_VOLUME_PERCENTAGE: number,
//   QC_STATUS: string,
// ): IAdmTableData => {
//   return {
//     base_table: BASE_TABLE,
//     count_in_base_table: COUNT_IN_BASE_TABLE,
//     mapped_table: MAPPED_TABLE,
//     count_of_mapped: COUNT_OF_MAPPED,
//     match_volume_percentage: MATCH_VOLUME_PERCENTAGE,
//     QC_STATUS,
//   }
// }
export default function AdmTable() {
  const [page, setPage] = React.useState(0) // Current page in pagination
  const [rowsPerPage, setRowsPerPage] = React.useState(10) // Rows to display per page

  const theme: any = useTheme()
  const classes = useStyles({ theme })
  // const [rows, setRows] = React.useState([]) // State to hold rows for the table
  const data: IAdmTableData[] = useAppSelector(admDataSelector) // Get data from Redux store

  // Transform incoming data into the format required for the table
  // React.useEffect(() => {
  //   const temp: any = data.map((row) => {
  //     return createData(
  //       row["base_table"],
  //       row["count_in_base_table"],
  //       row["mapped_table"],
  //       row["count_of_mapped"],
  //       row["match_volume_percentage"],
  //       row["QC_STATUS"],
  //     )
  //   })
  //   setRows(temp) // Set the transformed rows
  // }, [data]) // Dependency on data changes

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage) // Update to the new page
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value) // Update rows per page
    setPage(0) // Reset to the first page
  }

  // Function to render cell values based on the column type
  const renderCellValue = (column: any, value: any) => {
    // Handle percentage formatting and pass/fail indicators
    if (column.id === "match_volume_percentage") {
      return (
        <div className={classes.resultDiv}>
          {column.format && typeof value === "number"
            ? column.format(value)
            : value}
        </div>
      )
    }
    if (column.format && typeof value === "number") {
      return column.format(value)
    }
    if (value === "PASS") {
      return <PassDiv>Pass</PassDiv>
    }
    if (value === "FAIL") {
      return <FailedDiv>Failed</FailedDiv>
    }
    return value // Default case: return value as is
  }

  return (
    <Paper sx={{ width: "100%" }}>
      <TableContainer sx={{ maxHeight: 515 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead className={`${classes.root} ${classes.textAlign}`}>
            <TableRow>
              <TableCell
                align="center"
                className={`${classes.borderRight} ${classes.tableCell}`}
                colSpan={1}
              >
                {/* Placeholder for Country column */}
              </TableCell>
              <TableCell
                className={`${classes.wrapper} ${classes.borderRight} ${classes.tableCell}`}
                align="center"
                colSpan={2}
              >
                <div className={classes.heading}> Base Table details</div>
              </TableCell>
              <TableCell
                className={`${classes.wrapper} ${classes.borderRight} ${classes.tableCell}`}
                align="center"
                colSpan={2}
              >
                <div className={classes.heading}> Mapped Table details</div>
              </TableCell>
              <TableCell
                className={`${classes.wrapper} ${classes.borderRight} ${classes.tableCell}`}
                align="center"
                colSpan={2}
              >
                <div className={classes.heading}>Results</div>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                key={"id"}
                align={"center"}
                className={`${classes.wrapper} ${classes.borderRight} ${classes.tableCell}`}
              >
                <Typography variant="tableHeader"> S.No </Typography>
              </TableCell>
              {/* Render table headers from columns array */}
              {columns.map((column, i) => (
                <TableCell
                  key={uuidv4()}
                  align={column.align}
                  className={`${classes.wrapper} ${classes.tableCellWithMinWidth}`}
                  sx={{ borderRight: i % 2 !== 0 ? "1px solid lightgray" : "" }}
                >
                  <Typography variant="tableHeader">{column.label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Render rows for the current page */}
            {data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                    <TableCell
                      align={"center"}
                      className={classes.tableCell}
                      sx={{ borderRight: "1px solid lightgray" }}
                    >
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    {/* Render each cell in the row based on columns */}
                    {columns.map((column, i) => {
                      const value = row[column.id] // Get the cell value
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          className={`${classes.wrapper} ${classes.tableCellWithMinWidth}`}
                          sx={{
                            borderRight:
                              i % 2 !== 0 ? "1px solid lightgray" : "",
                          }}
                        >
                          {renderCellValue(column, value)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination component for navigating through pages */}
      <TablePagination
        rowsPerPageOptions={[10, 20]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  )
}
